/* DentPDR content pages: contact and application forms, technician directory, wishlist grid,
   article table of contents, policy navigation. Vanilla custom elements, loaded with defer. */
(() => {
  const root = () => (window.DP && window.DP.root) || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* Contact and technician application forms: client validation, topic preselect, submit state. */
  class ContactForm extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;
      this.touched = false;
      this.topic = this.querySelector('[data-dp-topic]');
      this.order = this.querySelector('[data-dp-order]');
      this.submitButton = this.querySelector('[data-dp-submit]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('input', () => { if (this.touched) this.validate(); });
      if (this.topic) {
        const wanted = (new URLSearchParams(window.location.search).get('topic') || '').toLowerCase();
        if (wanted) {
          const match = [...this.topic.options].find((option) =>
            option.dataset.key === wanted || option.text.trim().toLowerCase().replace(/\s+/g, '-') === wanted);
          if (match) this.topic.value = match.value;
        }
        this.topic.addEventListener('change', () => this.syncOrder());
        this.syncOrder();
      }
    }

    syncOrder() {
      if (!this.order) return;
      const key = this.topic.selectedOptions[0] && this.topic.selectedOptions[0].dataset.key;
      this.order.toggleAttribute('data-open', key === 'order-status' || key === 'returns');
    }

    validate() {
      let first = null;
      this.querySelectorAll('[data-field]').forEach((field) => {
        const input = field.querySelector('input, textarea');
        const error = field.querySelector('[data-error]');
        if (!input || !error) return;
        const kind = field.dataset.field;
        const value = input.value.trim();
        const bad = kind === 'email' ? !EMAIL.test(value) : !value;
        const message = bad ? this.dataset['error' + kind.charAt(0).toUpperCase() + kind.slice(1)] : '';
        error.hidden = !bad;
        error.querySelector('span').textContent = message || '';
        if (bad) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
        const described = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error.id);
        if (bad) described.push(error.id);
        if (described.length) input.setAttribute('aria-describedby', described.join(' '));
        else input.removeAttribute('aria-describedby');
        if (bad && !first) first = input;
      });
      return first;
    }

    onSubmit(event) {
      this.touched = true;
      const invalid = this.validate();
      if (invalid) {
        event.preventDefault();
        invalid.focus();
        return;
      }
      const button = this.submitButton;
      if (!button) return;
      const spinner = this.querySelector('template[data-dp-spinner]');
      // Disable after the browser has collected the form data, so the post still goes through.
      setTimeout(() => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = this.dataset.sending || '';
        if (spinner) button.prepend(spinner.content.cloneNode(true));
      }, 0);
    }
  }

  /* Technician directory: browser-side filters and the profile dialog. */
  class TechnicianDirectory extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-dp-tech-card]')];
      this.filters = this.querySelector('[data-dp-tech-filters]');
      this.grid = this.querySelector('[data-dp-tech-grid]');
      this.empty = this.querySelector('[data-dp-tech-empty]');
      this.count = this.querySelector('[data-dp-tech-count]');
      this.country = this.querySelector('[data-dp-tech-country]');
      this.experience = this.querySelector('[data-dp-tech-experience]');
      this.chips = [...this.querySelectorAll('[data-dp-tech-specialty]')];
      this.specialty = '';
      if (this.filters) {
        this.filters.hidden = false;
        if (this.country) this.country.addEventListener('change', () => this.apply());
        if (this.experience) this.experience.addEventListener('change', () => this.apply());
        this.chips.forEach((chip) => chip.addEventListener('click', () => {
          this.specialty = chip.dataset.dpTechSpecialty;
          this.apply();
        }));
        const clear = this.querySelector('[data-dp-tech-clear]');
        if (clear) clear.addEventListener('click', () => this.reset());
      }
      this.addEventListener('click', (event) => this.onClick(event));
      this.querySelectorAll('dialog').forEach((dialog) => dialog.addEventListener('close', () => {
        dialog.querySelectorAll('video').forEach((video) => video.pause());
        const play = dialog.querySelector('[data-dp-tech-play]');
        if (play) play.hidden = false;
      }));
    }

    reset() {
      this.specialty = '';
      if (this.country) this.country.value = '';
      if (this.experience) this.experience.value = '';
      this.apply();
    }

    matchesYears(years, range) {
      if (range === '') return true;
      if (range === '0') return years <= 5;
      if (range === '5') return years > 5 && years < 10;
      return years >= 10;
    }

    apply() {
      const country = this.country ? this.country.value : '';
      const range = this.experience ? this.experience.value : '';
      let shown = 0;
      this.cards.forEach((card) => {
        const ok = (!country || card.dataset.country === country)
          && (!this.specialty || card.dataset.specialty === this.specialty)
          && this.matchesYears(Number(card.dataset.years) || 0, range);
        card.hidden = !ok;
        if (ok) shown += 1;
      });
      this.chips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.dpTechSpecialty === this.specialty)));
      if (this.count) this.count.textContent = (shown === 1 ? this.count.dataset.one : this.count.dataset.other).replace('{n}', shown);
      if (this.empty) this.empty.hidden = shown > 0;
      if (this.grid) this.grid.hidden = shown === 0;
    }

    onClick(event) {
      const opener = event.target.closest('[data-dp-tech-open]');
      if (opener) {
        const dialog = this.querySelector('#' + opener.dataset.dpTechOpen);
        if (dialog && dialog.showModal) dialog.showModal();
        return;
      }
      const dialog = event.target.closest('dialog');
      if (!dialog) return;
      if (event.target === dialog || event.target.closest('[data-dp-tech-close]')) {
        dialog.close();
        return;
      }
      const play = event.target.closest('[data-dp-tech-play]');
      if (play) {
        const video = dialog.querySelector('video');
        if (video) {
          video.muted = true;
          video.play();
        }
        play.hidden = true;
      }
    }
  }

  /* Wishlist page: one card per handle in localStorage['dp:wishlist'], fetched through the Section Rendering API. */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-dp-wishlist-grid]');
      this.emptyPanel = this.querySelector('[data-dp-wishlist-empty]');
      this.countLine = this.querySelector('[data-dp-wishlist-count]');
      this.skeleton = this.querySelector('template[data-dp-wishlist-skeleton]');
      this.sync(this.read());
      document.addEventListener('dp:wishlist:change', (event) => this.sync(event.detail && event.detail.handles ? event.detail.handles : this.read()));
    }

    read() {
      try {
        const list = JSON.parse(window.localStorage.getItem('dp:wishlist') || '[]');
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    }

    drop(handle) {
      try {
        const list = this.read().filter((item) => item !== handle);
        window.localStorage.setItem('dp:wishlist', JSON.stringify(list));
      } catch (error) { /* storage unavailable: nothing to clean */ }
    }

    sync(handles) {
      this.handles = handles;
      const items = [...this.grid.children];
      items.forEach((item) => { if (!handles.includes(item.dataset.handle)) item.remove(); });
      handles.forEach((handle, index) => {
        let item = this.grid.querySelector('[data-handle="' + handle + '"]');
        if (!item) {
          item = document.createElement('div');
          item.dataset.handle = handle;
          item.className = 'dp-wishlist__item';
          if (this.skeleton) item.append(this.skeleton.content.cloneNode(true));
          this.load(handle, item);
        }
        if (this.grid.children[index] !== item) this.grid.insertBefore(item, this.grid.children[index] || null);
      });
      this.update();
    }

    async load(handle, item) {
      try {
        const response = await fetch(root() + 'products/' + encodeURIComponent(handle) + '?section_id=page-wishlist-card');
        if (!response.ok) throw new Error('missing');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = doc.querySelector('.shopify-section') || doc.body;
        if (!card.firstElementChild) throw new Error('empty');
        item.innerHTML = card.innerHTML;
      } catch (error) {
        item.remove();
        this.drop(handle);
        this.handles = this.handles.filter((value) => value !== handle);
        this.update();
      }
    }

    update() {
      const total = this.grid.children.length;
      this.emptyPanel.hidden = total > 0;
      this.countLine.hidden = total === 0;
      const template = total === 1 ? this.dataset.countOne : this.dataset.countOther;
      this.countLine.textContent = (template || '').replace('{n}', total);
    }
  }

  /* Article: table of contents from the h2 headings, scrollable tables and Best for / Avoid when boxes. */
  class ArticleToc extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-dp-article-body]');
      this.nav = this.querySelector('[data-dp-toc]');
      if (!this.body) return;
      this.enhanceTables();
      if (!this.nav) return;
      const headings = [...this.body.querySelectorAll('h2')];
      if (headings.length < 2) return;
      const list = this.nav.querySelector('[data-dp-toc-list]');
      const used = new Set();
      this.links = headings.map((heading) => {
        let id = heading.id || heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
        while (used.has(id)) id += '-2';
        used.add(id);
        heading.id = id;
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = '#' + id;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
        return { heading, link };
      });
      this.nav.hidden = false;
      const toggle = this.nav.querySelector('[data-dp-toc-toggle]');
      toggle.addEventListener('click', () => toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true')));
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { if (entry.isIntersecting) this.mark(entry.target); });
        }, { rootMargin: '0px 0px -70% 0px' });
        this.links.forEach(({ heading }) => observer.observe(heading));
      }
      this.mark(headings[0]);
    }

    mark(heading) {
      this.links.forEach((entry) => {
        if (entry.heading === heading) entry.link.setAttribute('aria-current', 'location');
        else entry.link.removeAttribute('aria-current');
      });
    }

    enhanceTables() {
      const check = this.querySelector('template[data-dp-icon-check]');
      const cross = this.querySelector('template[data-dp-icon-x]');
      this.body.querySelectorAll('table').forEach((table) => {
        const headCells = [...table.querySelectorAll('thead th, tr:first-child th')];
        const heads = headCells.map((cell) => cell.textContent.trim().toLowerCase());
        if (heads.length === 2 && heads[0] === 'best for' && heads[1] === 'avoid when') {
          const wrap = document.createElement('div');
          wrap.className = 'dp-article__procon';
          [0, 1].forEach((column) => {
            const box = document.createElement('div');
            const title = document.createElement('b');
            title.className = column ? 'is-avoid' : 'is-best';
            const icon = column ? cross : check;
            if (icon) title.append(icon.content.cloneNode(true));
            title.append(document.createTextNode(headCells[column].textContent.trim()));
            box.append(title);
            table.querySelectorAll('tbody tr').forEach((row) => {
              if (row.querySelector('th')) return;
              const cell = row.children[column];
              if (cell && cell.textContent.trim()) {
                const line = document.createElement('span');
                line.innerHTML = cell.innerHTML;
                box.append(line);
              }
            });
            wrap.append(box);
          });
          table.replaceWith(wrap);
          return;
        }
        const scroller = document.createElement('div');
        scroller.className = 'dp-article__scroll';
        table.replaceWith(scroller);
        scroller.append(table);
      });
    }
  }

  /* Workshop podcast of a guide (owner change 2026-10-24): play, seek, speed, transcript that follows the audio. */
  class PodcastEpisode extends HTMLElement {
    connectedCallback() {
      if (this.audio) return;
      this.audio = this.querySelector('[data-dp-pod-audio]');
      this.playBtn = this.querySelector('[data-dp-pod-play]');
      this.seek = this.querySelector('[data-dp-pod-seek]');
      this.now = this.querySelector('[data-dp-pod-now]');
      this.speedBtn = this.querySelector('[data-dp-pod-speed]');
      this.list = this.querySelector('[data-dp-pod-lines]');
      this.follow = this.querySelector('[data-dp-pod-follow]');
      this.lines = [...this.querySelectorAll('[data-t]')];
      this.at = -1;
      const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
      this.playBtn.addEventListener('click', () => (this.audio.paused ? this.play() : this.audio.pause()));
      this.seek.addEventListener('input', () => { this.audio.currentTime = +this.seek.value; this.paint(); });
      this.speedBtn.addEventListener('click', () => {
        const steps = [1, 1.25, 1.5, 2];
        this.audio.playbackRate = steps[(steps.indexOf(this.audio.playbackRate) + 1) % steps.length];
        this.speedBtn.textContent = `${this.audio.playbackRate}×`;
      });
      this.lines.forEach((line) => line.addEventListener('click', () => { this.audio.currentTime = +line.dataset.t; this.play(); }));
      this.audio.addEventListener('play', () => this.state(true));
      this.audio.addEventListener('pause', () => this.state(false));
      this.audio.addEventListener('ended', () => this.state(false));
      this.audio.addEventListener('loadedmetadata', () => { if (this.audio.duration) this.seek.max = Math.round(this.audio.duration); });
      this.audio.addEventListener('timeupdate', () => { this.now.textContent = fmt(this.audio.currentTime); this.paint(); });
    }
    play() {
      // one podcast at a time on the page
      document.querySelectorAll('podcast-episode audio').forEach((a) => { if (a !== this.audio) a.pause(); });
      this.audio.play();
    }
    state(playing) {
      this.toggleAttribute('data-playing', playing);
      this.playBtn.setAttribute('aria-label', playing ? this.playBtn.dataset.labelPause : this.playBtn.dataset.labelPlay);
    }
    paint() {
      const t = this.audio.currentTime;
      if (document.activeElement !== this.seek) this.seek.value = Math.round(t);
      this.seek.style.setProperty('--p', `${(t / (+this.seek.max || 1)) * 100}%`);
      let i = -1;
      this.lines.forEach((line, n) => { if (+line.dataset.t <= t) i = n; });
      if (i === this.at) return;
      if (this.lines[this.at]) this.lines[this.at].removeAttribute('aria-current');
      this.at = i;
      const line = this.lines[i];
      if (!line) return;
      line.setAttribute('aria-current', 'true');
      if (this.follow && this.follow.checked && this.list.offsetParent) {
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollTo({ top: line.offsetTop - this.list.clientHeight / 3, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
  }

  /* Starter kit equipment carousel: previous and next scroll one view; the buttons hide when everything fits. */
  class EquipRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.nav = this.querySelector('[data-rail-nav]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track || this.prev.dataset.bound) return;
      this.prev.dataset.bound = '1';
      const go = (d) => {
        const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: d * rtl * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      };
      this.prev.addEventListener('click', () => go(-1));
      this.next.addEventListener('click', () => go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = Math.abs(this.track.scrollLeft);
      this.nav.hidden = max <= 1;
      this.prev.disabled = pos <= 1;
      this.next.disabled = pos >= max - 1;
    }
  }

  if (!customElements.get('equip-rail')) customElements.define('equip-rail', EquipRail);
  if (!customElements.get('podcast-episode')) customElements.define('podcast-episode', PodcastEpisode);
  if (!customElements.get('contact-form')) customElements.define('contact-form', ContactForm);
  if (!customElements.get('technician-directory')) customElements.define('technician-directory', TechnicianDirectory);
  if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
  if (!customElements.get('article-toc')) customElements.define('article-toc', ArticleToc);

  document.addEventListener('change', (event) => {
    const select = event.target.closest && event.target.closest('[data-dp-policy-select]');
    if (select && select.value) window.location.assign(select.value);
  });

  const posted = document.querySelector('[data-dp-contact-success]');
  if (posted) (posted.querySelector('h2') || posted).focus();
})();
